import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getTransferStatus, getTransferPackage } from '../services/api'
import { normalizeTransfer } from '@/utils/normalize'
import Header from '@/components/Header'

interface TransferStatus {
  id: string
  transferNo: string
  recipient: string
  amount: number
  description: string
  status: 'pending' | 'processing' | 'success' | 'failed'
  transferMode: 'REAL_TIME' | 'USER_CONFIRM'
  createdAt: string
  updatedAt: string
  wechatTransferId?: string
  errorMessage?: string
}

export default function TransferStatus() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [transfer, setTransfer] = useState<TransferStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPolling, setIsPolling] = useState(true)
  const [pkg, setPkg] = useState<any>(null)
  const [pkgErr, setPkgErr] = useState<string | null>(null)

  useEffect(() => {
    const fetchStatus = async () => {
      if (!id) return

      try {
        const response = await getTransferStatus(id)
        setTransfer(normalizeTransfer(response.data))
        
        // 如果状态是success或failed，停止轮询
        if (response.data.status === 'success' || response.data.status === 'failed') {
          setIsPolling(false)
        }
      } catch (error) {
        console.error('获取转账状态失败:', error)
        alert('获取转账状态失败，请重试')
      } finally {
        setIsLoading(false)
      }
    }

    fetchStatus()

    // 轮询机制：如果状态还在处理中，每3秒查询一次
    let interval: NodeJS.Timeout
    if (isPolling && (transfer?.status === 'pending' || transfer?.status === 'processing')) {
      interval = setInterval(fetchStatus, 3000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [id, isPolling, transfer?.status])

  const handleFetchPackage = async () => {
    try {
      setPkgErr(null)
      if (!transfer?.transferNo) throw new Error('无转账单号')
      const r = await getTransferPackage(transfer.transferNo)
      setPkg(r.data?.packageInfo || null)
    } catch (e: any) {
      setPkgErr(e?.message || '获取确认信息失败')
    }
  }

  const handleOpenConfirm = () => {
    try {
      const url = (pkg && (pkg.jump_url || pkg.url)) || ''
      if (url && typeof window !== 'undefined') {
        window.open(url, '_blank')
        return
      }
      const text = JSON.stringify(pkg || {}, null, 2)
      navigator.clipboard && navigator.clipboard.writeText(text)
      alert('已复制确认信息，请在微信内打开进行确认')
    } catch {
      alert('无法打开或复制确认信息')
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <span className="text-green-600 text-2xl">✓</span>
          </div>
        )
      case 'failed':
        return (
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-600 text-2xl">✗</span>
          </div>
        )
      case 'processing':
        return (
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )
      default:
        return (
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 text-2xl">⏱</span>
          </div>
        )
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return '转账成功'
      case 'failed':
        return '转账失败'
      case 'processing':
        return '处理中'
      default:
        return '等待处理'
    }
  }

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'success':
        return '资金已成功转入收款人账户'
      case 'failed':
        return transfer?.errorMessage || '转账失败，请检查收款人信息或联系客服'
      case 'processing':
        return '正在处理您的转账请求，请稍候'
      default:
        return '系统正在准备处理您的转账请求'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wechat-green"></div>
      </div>
    )
  }

  if (!transfer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">转账记录不存在</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-wechat-green hover:text-wechat-dark"
          >
            返回控制台
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="转账状态" backTo="/dashboard" />

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg px-8 py-8">
          {/* Status Header */}
          <div className="text-center mb-8">
            {getStatusIcon(transfer.status)}
            <h2 className="mt-4 text-2xl font-bold text-gray-900">
              {getStatusText(transfer.status)}
            </h2>
            <p className="mt-2 text-gray-600">
              {getStatusDescription(transfer.status)}
            </p>
          </div>

          {/* Transfer Details */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">转账详情</h3>
            <dl className="grid grid-cols-1 gap-4">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">转账单号</dt>
                <dd className="text-sm text-gray-900 font-mono">{transfer.transferNo}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">收款人</dt>
                <dd className="text-sm text-gray-900">{transfer.recipient}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">转账金额</dt>
                <dd className="text-sm text-gray-900 font-medium">¥{transfer.amount.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">转账备注</dt>
                <dd className="text-sm text-gray-900">{transfer.description}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">转账方式</dt>
                <dd className="text-sm text-gray-900">
                  {transfer.transferMode === 'REAL_TIME' ? '实时到账' : '用户确认'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">创建时间</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(transfer.createdAt).toLocaleString('zh-CN')}
                </dd>
              </div>
              {transfer.wechatTransferId && (
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">微信单号</dt>
                  <dd className="text-sm text-gray-900 font-mono">{transfer.wechatTransferId}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md text-sm font-medium"
            >
              返回控制台
            </button>
            {transfer.status === 'failed' && (
              <button
                onClick={() => navigate('/transfer/create')}
                className="bg-wechat-green hover:bg-wechat-dark text-white px-6 py-2 rounded-md text-sm font-medium"
              >
                重新发起
              </button>
            )}
            {transfer.status === 'processing' && (
              <>
                {!pkg && (
                  <button
                    onClick={handleFetchPackage}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium"
                  >
                    获取确认信息
                  </button>
                )}
                {pkg && (
                  <button
                    onClick={handleOpenConfirm}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md text-sm font-medium"
                  >
                    确认收款
                  </button>
                )}
              </>
            )}
          </div>
          {pkgErr && (
            <div className="mt-4 text-sm text-red-600 text-center">{pkgErr}</div>
          )}
          {pkg && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">确认信息</h4>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-64">{JSON.stringify(pkg, null, 2)}</pre>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
